import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, BarChart, Bar, Legend } from "recharts";
import { Timer, Sparkles, AlertTriangle } from "lucide-react";
import { CardHeader, EmptyState, GlassCard, T, TYPE, CHART, SERIES, ChartTooltip, formatDate, labelSx, primaryButtonSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { Progress, has } from "../ui";
import { fieldSx } from "../inputs";

const STATUS = { active: ["#067647", "#DCFAE6"], future: ["#475467", "#F2F4F7"], completed: ["#026AA2", "#E0F2FE"] };

export const Burndown = ({ sprint, unitDefault = "points" }) => {
  const [unit, setUnit] = useState(unitDefault);
  const [data, setData] = useState(null);
  useEffect(() => {
    pm.burndown(sprint._id, unit).then(setData).catch(() => setData({ series: [] }));
  }, [sprint._id, unit, sprint.status]);
  return (
    <GlassCard>
      <CardHeader title="Burndown" subtitle={`Remaining ${unit} per day vs the ideal line from the committed scope${data ? ` (${data.committed})` : ""}`}
        action={<ToggleButtonGroup size="small" exclusive value={unit} onChange={(_, v) => v && setUnit(v)}>{["points", "count", "hours"].map((u) => <ToggleButton key={u} value={u} sx={{ textTransform: "none", fontSize: TYPE.micro }}>{u}</ToggleButton>)}</ToggleButtonGroup>} />
      <Box sx={{ height: 280, p: 2 }}>
        {!data ? <CircularProgress size={20} /> : !data.series.length ? (
          <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>The sprint needs start and end dates for a burndown.</Typography>
        ) : (
          <ResponsiveContainer>
            <LineChart data={data.series}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="day" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={CHART.tick} {...CHART.axisProps} width={34} />
              <RTooltip content={<ChartTooltip />} />
              <Legend />
              <Line dataKey="ideal" name="Ideal" stroke={T.inkMuted} strokeDasharray="5 4" dot={false} />
              <Line dataKey="remaining" name="Remaining" stroke={SERIES[4]} strokeWidth={2.5} dot={{ r: 2 }} connectNulls={false} />
              <Line dataKey="scope" name="Scope" stroke={SERIES[1]} strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </GlassCard>
  );
};

export const Velocity = ({ project }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    pm.report("velocity", { projectId: project._id }).then(setData).catch(() => setData(null));
  }, [project._id]);
  if (!data) return null;
  return (
    <GlassCard>
      <CardHeader title="Velocity" subtitle={data.note} action={data.averageCompleted !== null && <Chip size="small" label={`Average ${data.averageCompleted} ${data.unit}`} />} />
      <Box sx={{ height: 260, p: 2 }}>
        {!data.sprints.length ? <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Complete a sprint to see velocity.</Typography> : (
          <ResponsiveContainer>
            <BarChart data={data.sprints}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="name" tick={CHART.tick} {...CHART.axisProps} />
              <YAxis tick={CHART.tick} {...CHART.axisProps} width={34} />
              <RTooltip content={<ChartTooltip />} />
              <Legend />
              <Bar dataKey="committed" name="Committed" fill={SERIES[1]} radius={[4, 4, 0, 0]} />
              <Bar dataKey="completed" name="Completed" fill={SERIES[2]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </GlassCard>
  );
};

const Planning = ({ sprint, project, onSaved }) => {
  const ctx = usePm();
  const [plan, setPlan] = useState(null);
  const [cap, setCap] = useState({});
  useEffect(() => {
    pm.sprintPlanning(sprint._id).then((p) => {
      setPlan(p);
      setCap(Object.fromEntries(p.people.map((x) => [String(x.memberId), { hours: x.capacityHours, points: x.capacityPoints, name: x.name }])));
    }).catch(() => {});
  }, [sprint._id, sprint.updatedAt]);
  if (!plan) return null;
  const editable = has(project, "sprint.update") && sprint.status !== "completed";
  const save = async () => {
    try {
      await pm.updateSprint(sprint._id, { capacity: Object.entries(cap).map(([memberId, c]) => ({ memberId, name: c.name, hours: Number(c.hours) || 0, points: Number(c.points) || 0 })) });
      ctx.notify("Capacity saved");
      onSaved();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <GlassCard>
      <CardHeader title="Planning & capacity" subtitle={`Committed ${plan.committed.points} pts · ${plan.committed.hours}h estimated · capacity ${plan.capacity.points} pts / ${plan.capacity.hours}h`}
        action={editable && <Button size="small" variant="contained" sx={primaryButtonSx} onClick={save}>Save capacity</Button>} />
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>{["Person", "Issues", "Points", "Estimate (h)", "Capacity (h)", "Capacity (pts)", ""].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {plan.people.map((p) => (
              <TableRow key={String(p.memberId)}>
                <TableCell sx={{ fontSize: TYPE.small }}>{p.name}</TableCell>
                <TableCell>{p.issues}</TableCell>
                <TableCell>{p.points}</TableCell>
                <TableCell>{p.committedHours}</TableCell>
                <TableCell>{editable ? <TextField size="small" type="number" value={cap[String(p.memberId)]?.hours ?? 0} onChange={(e) => setCap({ ...cap, [String(p.memberId)]: { ...cap[String(p.memberId)], hours: e.target.value } })} sx={{ ...fieldSx, width: 80 }} /> : p.capacityHours}</TableCell>
                <TableCell>{editable ? <TextField size="small" type="number" value={cap[String(p.memberId)]?.points ?? 0} onChange={(e) => setCap({ ...cap, [String(p.memberId)]: { ...cap[String(p.memberId)], points: e.target.value } })} sx={{ ...fieldSx, width: 80 }} /> : p.capacityPoints}</TableCell>
                <TableCell>{p.overloaded && <Chip size="small" icon={<AlertTriangle size={12} />} label="Over capacity" sx={{ height: 20, bgcolor: "#FEE4E2", color: "#B42318", fontSize: "0.66rem" }} />}</TableCell>
              </TableRow>
            ))}
            {plan.unassigned.issues > 0 && <TableRow><TableCell sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Unassigned</TableCell><TableCell>{plan.unassigned.issues}</TableCell><TableCell>{plan.unassigned.points}</TableCell><TableCell colSpan={4} /></TableRow>}
          </TableBody>
        </Table>
      </Box>
    </GlassCard>
  );
};

const SprintAi = ({ sprint, project }) => {
  const ctx = usePm();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!ctx.settings.ai?.enabled || !has(project, "ai.use")) return null;
  const g = data?.generated || {};
  return (
    <GlassCard sx={{ borderColor: "#E9D7FE" }}>
      <CardHeader icon={Sparkles} title="AI sprint summary" action={<Button size="small" disabled={busy} onClick={async () => { setBusy(true); setError(""); try { setData(await pm.sprintAi(sprint._id)); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); } }}>{busy ? "…" : "Summarise"}</Button>} />
      {(error || data) && (
        <Box sx={{ p: 2 }}>
          {error && <Alert severity="warning">{error}</Alert>}
          {data && (
            <>
              <Typography sx={{ fontSize: TYPE.small, mb: 1 }}>{g.summary}</Typography>
              {["achievements", "notDone", "risks", "suggestions"].filter((k) => g[k]?.length).map((k) => (
                <Box key={k}><Typography sx={{ ...labelSx, fontSize: "0.66rem" }}>{k.replace(/([A-Z])/g, " $1")}</Typography><ul style={{ margin: "2px 0 8px", paddingLeft: 18, fontSize: 13 }}>{g[k].map((x, i) => <li key={i}>{x}</li>)}</ul></Box>
              ))}
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Measured: {data.facts.done}/{data.facts.issues} issues done · {data.facts.overdue?.length} overdue</Typography>
            </>
          )}
        </Box>
      )}
    </GlassCard>
  );
};

const Sprints = ({ project, version }) => {
  const [sprints, setSprints] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    pm.sprints(project._id).then((rows) => {
      setSprints(rows);
      setSelected((cur) => (cur && rows.find((s) => s._id === cur._id)) || rows.find((s) => s.status === "active") || rows.find((s) => s.status === "completed") || rows[0] || null);
    }).catch((err) => setError(errorMessage(err)));
  }, [project._id, version, tick]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!sprints) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  if (!sprints.length) return <GlassCard><EmptyState icon={Timer} title="No sprints yet" description="Create sprints from the backlog." /></GlassCard>;
  const s = selected;
  const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "260px minmax(0,1fr)" }, gap: 2.5 }}>
      <GlassCard sx={{ p: 1, alignSelf: "start" }}>
        {sprints.map((x) => (
          <Box key={x._id} onClick={() => setSelected(x)} sx={{ p: 1.25, borderRadius: "8px", cursor: "pointer", bgcolor: s?._id === x._id ? T.brandSoft : "transparent", "&:hover": { bgcolor: T.surfaceMuted } }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography noWrap sx={{ fontSize: TYPE.small, fontWeight: 700, flexGrow: 1 }}>{x.name}</Typography>
              <Chip size="small" label={x.status} sx={{ height: 18, fontSize: "0.62rem", color: STATUS[x.status][0], bgcolor: STATUS[x.status][1] }} />
            </Stack>
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{x.startDate ? `${formatDate(x.startDate)} – ${formatDate(x.endDate)}` : "Not scheduled"}</Typography>
          </Box>
        ))}
      </GlassCard>
      {s && (
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <GlassCard sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle }}>{s.name}</Typography>
            {s.goal && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 0.5 }}>Goal: {s.goal}</Typography>}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mt: 2 }}>
              <Box><Typography sx={labelSx}>Committed</Typography><Typography sx={{ fontWeight: 700 }}>{s.committed?.points ?? 0} pts · {s.committed?.count ?? 0} issues</Typography></Box>
              <Box><Typography sx={labelSx}>Completed</Typography><Typography sx={{ fontWeight: 700 }}>{s.status === "completed" ? `${s.completed?.points} pts · ${s.completed?.count}` : "in progress"}</Typography></Box>
              <Box><Typography sx={labelSx}>Added after start</Typography><Typography sx={{ fontWeight: 700 }}>{s.addedAfterStart?.count || 0} ({s.addedAfterStart?.points || 0} pts)</Typography></Box>
              <Box><Typography sx={labelSx}>Removed after start</Typography><Typography sx={{ fontWeight: 700 }}>{s.removedAfterStart?.count || 0} ({s.removedAfterStart?.points || 0} pts)</Typography></Box>
            </Box>
            {s.status === "completed" && <Box sx={{ mt: 2 }}><Progress value={pct(s.completed?.points, s.committed?.points)} label="Completion vs commitment" color="#12B76A" /></Box>}
          </GlassCard>
          {s.status !== "future" && <Burndown sprint={s} unitDefault={project.estimation === "hours" ? "hours" : project.estimation === "count" ? "count" : "points"} />}
          <SprintAi sprint={s} project={project} />
          <Planning sprint={s} project={project} onSaved={() => setTick((t) => t + 1)} />
          <Velocity project={project} />
        </Stack>
      )}
    </Box>
  );
};

export default Sprints;
